import { useState } from 'react';
import { Swatch, Sketch } from '@uiw/react-color';
import AddIcon from '@mui/icons-material/Add';
import ColorizeIcon from '@mui/icons-material/Colorize';
import styles from './ColorPickerPanel.module.scss';

const hasEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

// Tighter circles: 22px swatch with a 1px margin (was 2px) so there's less gap
// between the color circles. 10 columns × (22 + 1*2) = 240px row width.
const RECT_STYLE = { width: 22, height: 22, margin: 1, borderRadius: '50%' as const };
const SWATCH_CONTAINER_STYLE = { width: 240, flexWrap: 'wrap' as const, gap: 0 };

export interface ColorPickerPanelProps {
    /** Currently applied color — drives the active-swatch highlight and seeds
     *  the custom dialog. */
    value?: string;
    /** Color shown in the "current" preview dot. Defaults to `value`. */
    previewColor?: string;
    palette: string[];
    recents: string[];
    /** Apply a final color (palette / recent / custom-OK / eyedropper). The
     *  parent owns the editor mutation + popover close. */
    onPick: (hex: string) => void;
    /** Record a color in the shared recent history. */
    pushRecent: (hex: string) => void;
    /** Clear / reset to default. */
    onClear: () => void;
    clearLabel?: string;
    enableEyeDropper?: boolean;
}

export function ColorPickerPanel({
    value,
    previewColor,
    palette,
    recents,
    onPick,
    pushRecent,
    onClear,
    clearLabel = 'Default',
    enableEyeDropper = true,
}: ColorPickerPanelProps) {
    const [customOpen, setCustomOpen] = useState(false);
    // Draft color for the custom dialog — NOT applied to the document until OK.
    const [draft, setDraft] = useState<string>(value || '#000000');

    const dot = previewColor || value || '#37352f';

    const openCustom = () => {
        setDraft(value || '#000000');
        setCustomOpen(true);
    };
    const confirmCustom = () => {
        if (draft) {
            pushRecent(draft);
            onPick(draft);
        }
        setCustomOpen(false);
    };
    const cancelCustom = () => setCustomOpen(false);

    const pickEyeDropper = async () => {
        try {
            const result = await new (window as any).EyeDropper().open();
            if (result?.sRGBHex) {
                pushRecent(result.sRGBHex);
                onPick(result.sRGBHex);
            }
        } catch {
            /* user cancelled the eyedropper */
        }
    };

    return (
        <div className={styles.panel}>
            <Swatch
                colors={palette}
                color={value}
                rectProps={{ style: RECT_STYLE }}
                style={SWATCH_CONTAINER_STYLE}
                onChange={(_hsva, color) => onPick(color.hex)}
            />

            <div className={styles.sectionLabel}>Custom</div>
            <div className={styles.customRow}>
                <span className={styles.currentDot} style={{ background: dot }} aria-hidden />
                <button type="button" className={styles.customBtn} title="Custom color" aria-label="Custom color" onClick={openCustom}>
                    <AddIcon fontSize="small" />
                </button>
                {enableEyeDropper && hasEyeDropper && (
                    <button
                        type="button"
                        className={styles.customBtn}
                        title="Pick from screen"
                        aria-label="Eyedropper"
                        onClick={pickEyeDropper}
                    >
                        <ColorizeIcon fontSize="small" />
                    </button>
                )}
            </div>

            {recents.length > 0 && (
                <>
                    <div className={styles.sectionLabel}>Recent</div>
                    <Swatch
                        colors={recents}
                        color={value}
                        rectProps={{ style: RECT_STYLE }}
                        style={SWATCH_CONTAINER_STYLE}
                        onChange={(_hsva, color) => {
                            // Re-promote in history AND apply.
                            pushRecent(color.hex);
                            onPick(color.hex);
                        }}
                    />
                </>
            )}

            <button type="button" className={styles.clear} onClick={onClear}>
                {clearLabel}
            </button>

            {customOpen && (
                <div className={styles.customDialog} role="dialog" aria-label="Custom color picker">
                    <Sketch
                        color={draft}
                        disableAlpha
                        presetColors={false}
                        style={{ width: 240, boxShadow: 'none' }}
                        onChange={(color) => setDraft(color.hex)}
                    />
                    <div className={styles.dialogActions}>
                        <button type="button" className={styles.dialogCancel} onClick={cancelCustom}>
                            Cancel
                        </button>
                        <button type="button" className={styles.dialogOk} onClick={confirmCustom}>
                            OK
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
